import { useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { isSupportedLocale } from "../i18n/messages.js";
import { useCurrentUser } from "./use-auth.js";
import {
  applyFeedbackDisabledLocal,
  applyModalDismissedLocal,
  useFeedbackPreferences,
} from "./use-feedback-preferences.js";
import { useMarkerFormat, writeMarkerFormatLocal } from "./use-marker-format.js";
import { useTheme, writeThemeLocal } from "./use-theme.js";

/**
 * Pulls the authenticated user's saved preferences (theme, language,
 * markerFormat, the feedback-explainer opt-outs) into the local stores after
 * the session resolves. This is the "DB → UI" half of the bi-directional
 * sync; the toggle hooks (useTheme / useLanguage / useMarkerFormat /
 * useFeedbackPreferences) push the other direction via patchUserSettings.
 *
 * Mount once near the top of the tree (Shell). The writes are guarded
 * against the equal-value case so they do not loop with the PATCH callbacks.
 */
export function useUserSettingsSync(): void {
  const { data: user } = useCurrentUser();
  const { theme } = useTheme();
  const { markerFormat } = useMarkerFormat(user?.tier ?? "anon");
  const { modalDismissed, feedbackDisabled } = useFeedbackPreferences();
  const params = useParams<{ lang?: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // One-shot server→local mirror. After the initial sync per user instance,
  // local is the source of truth — re-applying user.X here would race with
  // the user's own clicks (overwriting an in-flight optimistic change) and
  // with cross-tab storage events (overwriting another tab's authoritative
  // mirror). Cross-device updates are picked up on the next page load, when
  // this effect re-runs with a fresh user.id gate. Regression coverage:
  // use-user-settings-sync.test.tsx.
  //
  // The language bounce lives inside the same one-shot gate: the DB language
  // is mirrored into the URL exactly once, when the user's session resolves
  // (login / first load) — after that, the URL locale is authoritative. This
  // avoids two bugs the old "runs on every URL change" version had: it
  // stomped deep links in a non-default language back to the DB language on
  // every nav, and it fought the locale switcher (navigate → bounce → patch
  // → navigate), causing visible flicker. Cross-device language changes are
  // picked up on the next load, same as theme/markerFormat above.
  const syncedForUserId = useRef<string | null>(null);

  useEffect(() => {
    if (!user) {
      syncedForUserId.current = null; // reset gate on logout so next login re-syncs
      return;
    }

    if (syncedForUserId.current !== user.id) {
      syncedForUserId.current = user.id;
      if (user.theme !== theme) writeThemeLocal(user.theme);
      if (user.markerFormat !== markerFormat) writeMarkerFormatLocal(user.markerFormat);

      // Feedback opt-outs: deliberately NOT the unconditional mirror above.
      // Decision (feedback-modal handoff, open question "anon device vs.
      // account - which wins"): the device's local flag and the account's
      // server flag OR together, adopting the server's `true` when local is
      // still `false` but never the reverse. A device that already said
      // "stop asking" must not start asking again just because a login
      // happened - on a shared machine that could be a DIFFERENT account
      // that never set anything, and re-showing an explainer someone
      // already dismissed is the more harmful direction (see
      // use-feedback-preferences.ts's docstring): sparing a question
      // someone declined is harmless, asking it again is not. The
      // account's `true` still propagates to a fresh device (first login
      // there has local=false, so the OR picks up the server's `true`),
      // and an explicit re-enable from Account settings writes `false` to
      // BOTH layers via setModalDismissed/setFeedbackDisabled - only that
      // deliberate action, never this passive sync, turns dismissal back
      // off.
      if (user.feedbackModalDismissed && !modalDismissed) applyModalDismissedLocal(true);
      if (user.feedbackDisabled && !feedbackDisabled) applyFeedbackDisabledLocal(true);

      const urlLang = params.lang;
      if (urlLang && isSupportedLocale(urlLang) && user.language !== urlLang) {
        const parts = location.pathname.split("/").filter(Boolean);
        if (parts.length > 0 && isSupportedLocale(parts[0] ?? "")) {
          parts[0] = user.language;
        } else {
          parts.unshift(user.language);
        }
        navigate(`/${parts.join("/")}${location.search}${location.hash}`, { replace: true });
      }
    }
  }, [
    user,
    theme,
    markerFormat,
    modalDismissed,
    feedbackDisabled,
    params.lang,
    navigate,
    location,
  ]);
}
